import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { handleIncomingMessage, handleMessageEdit, handleMessageRemove, handleReadMessageCallback, handleReceiverReadMessage, handleStatusChange } from "store/actions/clientMessaging";
import { WEBSOCKET_CONNECTION_URL } from "store/services/apiPath";
import chimeSound from "Assets/sounds/notification-chime.mp3";
import { getConversationsList } from "store/services/clientMessagingService";
import { Slide, toast } from 'react-toastify';
import ToastNotification from 'components/shared/ToastNotification';
import { FULL_NAME } from 'helpers/common';
import { useLocation } from 'react-router-dom';

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.Auth.user);
  const practSettings = useSelector((state) => state.AccountSetting.accountSettingDetail_);
  const conversations = useSelector((state) => state.ClientMessaging.conversationList);

  const [isConnected, setIsConnected] = useState(false);
  const [isChatEnabled, setChatEnabled] = useState(true);
  const [documentHidden, setDocumentHidden] = useState(false);

  const socketRef = useRef(null);
  const pendingAcks = useRef({});
  const chimeRef = useRef(null);
  const conversationsRef = useRef(conversations);

  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);

  const audioChimeEnabledRef = useRef(false);
  const location = useLocation();
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);


  useEffect(() => {
    audioChimeEnabledRef.current = practSettings?.new_chat_msg_notification === "enable";
  }, [practSettings]);

  const unreadMessagesCount = useMemo(() => {
    if (!conversations?.length) {
      return 0;
    }
    return conversations.reduce((acc, conv) => {
      return acc + (conv?.unread_count || 0);
    }, 0)
  }, [conversations])

  useEffect(() => {
    if(!userInfo?.token || !practSettings?.messaging_status) {
      return;
    }

    if (
      practSettings?.messaging_status === "enable"
    ) {
      connectSocket();
      setChatEnabled(true);
    } else {
      setChatEnabled(false);
    }

    return () => {
      socketRef.current?.close();
      clearTimeout(reconnectTimeoutRef.current);
      // clearInterval(pingIntervalRef.current);
    };
  }, [userInfo?.token, practSettings?.messaging_status]);


  useEffect(() => {
    // Load the audio chime once
    chimeRef.current = new Audio(chimeSound);
    chimeRef.current.load();
  }, []);

  useEffect(() => {
    if (!userInfo?.token) {
      return;
    }
    dispatch(getConversationsList());
  }, [userInfo?.token]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setDocumentHidden(document.hidden);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    document.title = documentHidden && unreadMessagesCount > 0
      ? `(${unreadMessagesCount}) New Message`
      : "Nutri-Q";
  }, [unreadMessagesCount, documentHidden]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const notify = useCallback(async (data) => {
    if (locationRef.current.pathname === "/chat") return;

    const conversations = conversationsRef.current;
    if(!conversations?.length) return;

    const sender = await conversations.find((conv) => conv.sender_id === data.sender_id);
    if(!sender) return;

    const clientName =  await FULL_NAME(sender.client_first_name, sender.client_last_name);

    let parsedMessage;
    try {
      parsedMessage = JSON.parse(data.message);
    } catch (error) {
      console.error("Failed to parse message JSON:", error);
      return;
    }
    toast(
      <ToastNotification
        name={clientName}
        message={parsedMessage.text}
        icon="/icon.png"
      />,
      {
        position: "top-right",
        autoClose: true,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Slide,
        className: ' border border-purple-600/40 border-r rounded-3',
      }
    );
  }, [])

  const connectSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
    }

    const accessToken = userInfo?.token;
    const wsUrl = `${WEBSOCKET_CONNECTION_URL}?token=${accessToken}`;
    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
    };

    socketRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.action === 'messageAck' && data.idempotencyId) {
          const callbackEntry = pendingAcks.current[data.idempotencyId];
          if (callbackEntry) {
            callbackEntry.onSuccess?.(data);
            delete pendingAcks.current[data.idempotencyId];
          }
        } else if (data.action === "active_status") {
          dispatch(handleStatusChange(data));
        } else if (data.action === "msgRead") {
          dispatch(handleReadMessageCallback(data));
        } else if (data.action === "sentMsgReadByReceiver") {
          dispatch(handleReceiverReadMessage(data));
        } else if (data.action === "editMessage") {
          dispatch(handleMessageEdit(data));
        } else if (data.action === "removeMessage") {
          dispatch(handleMessageRemove(data));
        } else {
          dispatch(handleIncomingMessage(data));

          notify(data)

          // Play chime for a new message
          if (document.hidden && audioChimeEnabledRef.current && chimeRef.current) {
            chimeRef.current.play().catch(err => {
              console.warn("Chime play failed:", err);
            });
          }
        }
      } catch (err) {
        console.error("Error in incoming message handler", err);
      }
    };

    socketRef.current.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    socketRef.current.onclose = (event) => {
      console.log(`🔌 WebSocket closed: code=${event.code}, reason=${event.reason}`);
      setIsConnected(false);
      // clearInterval(pingIntervalRef.current);

      // Only reconnect on unexpected closure
      const shouldReconnect = [1006, 1011, 1001].includes(event.code); // 1006: abnormal, 1011: internal error, 1001: going away

      if (shouldReconnect && reconnectAttemptsRef.current < 5) {
        const timeout = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 10000);
        console.log(`⏳ Attempting reconnect #${reconnectAttemptsRef.current + 1} in ${timeout / 1000}s...`);
        reconnectTimeoutRef.current = setTimeout(connectSocket, timeout);
        reconnectAttemptsRef.current += 1;
      } else {
        console.log("Not reconnecting, either clean close or retry limit exceeded");
      }
    };
  };

  const sendMessage = (message, onSuccess, onError) => {
    const idempotencyId = message.idempotencyId;

    // Store the callback
    if (onSuccess || onError) {
      pendingAcks.current[idempotencyId] = { onSuccess, onError };
    }

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not open');
      if (onError) onError('Socket not open');
    }
  };

  return (
    <WebSocketContext.Provider value={{ sendMessage, isConnected, unreadMessagesCount, isChatEnabled }}>
      {children}
    </WebSocketContext.Provider>
  );
};
